import { zodResolver } from '@hookform/resolvers/zod';
import { Globe } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCountries } from '../hooks/useCountries';
import { useCountryById } from '../hooks/useCountryById';
import { useTerritoriesByLevel } from '../hooks/useTerritoriesByLevel';
import { useUpdateCountry } from '../hooks/useUpdateCountry';
import {
  TERRITORIAL_LEVELS,
  updateCountryWithTerritorySchema,
  type UpdateCountryWithTerritoryFormValues,
} from '../schemas/country-schema';
import { CURRENCIES, TerritorialLevel } from '../types/countries.types';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { PaginatedSelect } from '@/shared/components/ahkinia/PaginatedSelect/paginated-select';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface EditCountryModalProps {
  disableChild?: boolean;
  countryId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LEVEL_LABELS: Record<string, string> = {
  [TerritorialLevel.Pais]: 'País',
  [TerritorialLevel.Estado]: 'Estado / Provincia',
  [TerritorialLevel.Municipio]: 'Municipio',
  [TerritorialLevel.Localidad]: 'Localidad',
};

function getParentLevel(level: TerritorialLevel): TerritorialLevel | undefined {
  switch (level) {
    case TerritorialLevel.Estado:
      return TerritorialLevel.Pais;
    case TerritorialLevel.Municipio:
      return TerritorialLevel.Estado;
    case TerritorialLevel.Localidad:
      return TerritorialLevel.Municipio;
    default:
      return undefined;
  }
}

export function EditCountryModal({
  countryId,
  open,
  onOpenChange,
  disableChild,
}: EditCountryModalProps) {
  const { updateCountry, isUpdating } = useUpdateCountry();
  // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
  const { country } = useCountryById(countryId);
  const { countries } = useCountries({ pageSize: 100 });
  const [pendingValues, setPendingValues] = useState<UpdateCountryWithTerritoryFormValues | null>(
    null,
  );

  const hasCurrencyDependency = !!(country?.regionTypes && country.regionTypes.length > 0);
  const hasChildren = useMemo(
    () => countries.some((c) => c.parentId === countryId),
    [countries, countryId],
  );

  const form = useForm<UpdateCountryWithTerritoryFormValues>({
    resolver: zodResolver(updateCountryWithTerritorySchema),
    defaultValues: {
      officialName: '',
      commonName: '',
      currencyCode: '',
    },
  });

  const selectedLevel = form.watch('level');
  const parentLevel = getParentLevel(selectedLevel as TerritorialLevel);
  const { fetchFn, renderItem } = useTerritoriesByLevel(parentLevel);

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
    if (country && open) {
      form.reset({
        officialName: country.officialName,
        commonName: country.commonName,
        currencyCode: country.currency,
        level: country.level,
        parentId: country.parentId ?? '',
      });
    }
  }, [country, open, form]);

  const onSubmit = (values: UpdateCountryWithTerritoryFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = () => {
    if (!pendingValues) return;
    const updateData = hasCurrencyDependency
      ? { ...pendingValues, currencyCode: country!.currency }
      : pendingValues;

    updateCountry(
      { id: countryId, data: updateData },
      {
        onSuccess: () => {
          Notification.success({ title: 'Territorio actualizado exitosamente' });
          setPendingValues(null);
          onOpenChange(false);
        },
        onError: () => {
          setPendingValues(null);
        },
      },
    );
  };

  const handleClose = () => {
    if (!isUpdating) {
      form.reset();
      onOpenChange(false);
    }
  };

  const handleLevelChange = (value: string) => {
    form.setValue('level', value as TerritorialLevel);
    if (value === TerritorialLevel.Pais) {
      form.setValue('parentId', '');
    }
  };

  const canEditLevel = !hasChildren;

  if (!country) {
    return (
      <ModalForm
        open={open}
        onOpenChange={handleClose}
        title="Editar territorio"
        className="sm:max-w-[425px]"
      >
        <div className="py-8" aria-hidden />
      </ModalForm>
    );
  }

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<Globe />}
        onOpenChange={handleClose}
        title="Editar territorio"
        description="Modifica la información del territorio seleccionado."
        className="sm:max-w-[425px]"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isUpdating}
                data-testid="cancel-edit-country-btn"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isUpdating}
                data-testid="update-country-btn"
              >
                Guardar cambios
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <FormField
          control={form.control}
          name="level"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SelectInput
                  label="Nivel territorial"
                  value={field.value ?? TerritorialLevel.Pais}
                  onValueChange={handleLevelChange}
                  disabled={!canEditLevel || !!disableChild || isUpdating}
                  placeholder="Seleccionar nivel"
                  error={
                    !canEditLevel
                      ? 'No se puede cambiar el nivel porque tiene territorios hijos.'
                      : undefined
                  }
                  data-testid="edit-country-level-select"
                >
                  {TERRITORIAL_LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {LEVEL_LABELS[level] ?? level}
                    </SelectItem>
                  ))}
                </SelectInput>
              </FormControl>
            </FormItem>
          )}
        />

        {!disableChild &&
          selectedLevel !== undefined &&
          selectedLevel !== TerritorialLevel.Pais && (
            <FormField
              control={form.control}
              name="parentId"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Territorio padre</FormLabel>
                  <FormControl>
                    <PaginatedSelect
                      disabled={isUpdating}
                      key={parentLevel}
                      value={value}
                      onChange={onChange}
                      fetchFn={fetchFn}
                      renderItem={renderItem}
                      pageSize={10}
                      placeholder="Seleccionar territorio padre"
                      label=""
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

        <FormField
          control={form.control}
          name="officialName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Nombre oficial"
                  placeholder="Ej: Colombia"
                  data-testid="edit-country-official-name-input"
                  disabled={isUpdating}
                  error={form.formState.errors.officialName?.message}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="commonName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Nombre común / alias"
                  placeholder="Ej: Colombia"
                  data-testid="edit-country-common-name-input"
                  disabled={isUpdating}
                  error={form.formState.errors.commonName?.message}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {country.level === TerritorialLevel.Pais && (
          <Input
            label="Código ISO"
            value={country.isoCode}
            disabled
            readOnly
            data-testid="edit-country-isocode-input"
          />
        )}

        <FormField
          control={form.control}
          name="currencyCode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SelectInput
                  label="Moneda base"
                  value={field.value.length > 0 ? field.value : country.currency}
                  onValueChange={field.onChange}
                  disabled={hasCurrencyDependency || isUpdating}
                  placeholder="Seleccionar moneda"
                  error={
                    hasCurrencyDependency
                      ? 'No se puede editar este campo debido a dependencias.'
                      : form.formState.errors.currencyCode?.message
                  }
                  data-testid="edit-country-currency-select"
                >
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.name} ({currency.symbol})
                    </SelectItem>
                  ))}
                </SelectInput>
              </FormControl>
            </FormItem>
          )}
        />
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isUpdating && !v && setPendingValues(null)}
        title="Confirmar cambios"
        description={
          <span>
            Se actualizará el territorio con nombre oficial{' '}
            <span className="font-medium text-light">{pendingValues?.officialName}</span> y nombre
            común <span className="font-medium text-light">{pendingValues?.commonName}</span>.
          </span>
        }
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isUpdating}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
